/*======================================================================================================================
  * Bluebell: src/app/state/results-view/results-view.effects.ts
  * Last Edited: 30/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  loadSandboxResults,
  reloadAllResults,
  resultsComponentLoad,
  resultsComponentRefresh,
} from '@state/results/results.actions';
import { LastUsedForceTableSettings } from '@state/account/account.interfaces';
import { ApiService } from '@state/api.service';
import { selectCurrentProjectId } from '@state/projects/project.selectors';
import {
  clearSelectedResultsView,
  deleteResultsView,
  deleteResultsViewFailure,
  deleteResultsViewSuccess,
  listResultsViews,
  listResultsViewsFailure,
  listResultsViewsSuccess,
  pinResultsView,
  pinResultsViewFailure,
  pinResultsViewSuccess,
  setResultsViewAsProjectDefault,
  setResultsViewAsProjectDefaultFailure,
  setResultsViewAsProjectDefaultSuccess,
  reloadSelectedResultsView,
  selectProjectDefaultResultsView,
  selectResultsView,
  selectResultsViewFailure,
  selectResultsViewSuccess,
  setCurrentView,
  updateResultsView,
  updateResultsViewFailure,
  updateResultsViewSuccess,
  createResultsView,
  createResultsViewSuccess,
  createResultsViewFailure,
  resultsResolverSelectDefaultView,
  setLastUsedResultsView,
  setLastUsedResultsViewSuccess,
  setLastUsedResultsViewFailure,
  setCurrentViewDeltaBaseline,
  toggleCurrentViewDeltaToLeadCase, reorderPinnedViews, reorderPinnedViewsSuccess, reorderPinnedViewsFailure,
} from '@state/results-view/results-view.actions';
import {
  ListResultsViewResponse,
  ResultsView,
  ResultsViewListData,
} from '@state/results-view/results-view.interfaces';
import {
  selectAllResultsViews,
  selectSelectedResultsView,
} from '@state/results-view/results-view.selectors';
import { selectSandbox } from '@state/sandbox/sandbox.actions';
import { Sandbox } from '@state/sandbox/sandbox.interfaces';
import { selectSandboxEntities, selectSelectedSandboxId } from '@state/sandbox/sandbox.selectors';
import { filter, map, switchMap } from 'rxjs/operators';
import { EffectHandlerService } from '@app/services/effect-handler.service';


@Injectable()
export class ResultsViewEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private store = inject(Store);
  private handler = inject(EffectHandlerService);


  /**
   * CREATE ACTIONS
   */

  // Create the selected results view
  createView$ = createEffect(() => this.actions$.pipe(
    ofType(createResultsView),
    switchMap(({ view }) => this.api.createForceTableSettings(view).pipe(
      this.handler.effectResponse({
        retry: createResultsView({ view }),
        success: (selectedView: ResultsView) => createResultsViewSuccess({ selectedView }),
        failure: createResultsViewFailure(),
        failureMessage: 'Failed to create results view',
      })
    ))
  ));

  // /**
  //  * RETRIEVE ACTIONS
  //  */
  // loadViewsOnProgrammeSelect$ = createEffect(() => this.actions$.pipe(
  //   ofType(ProgrammeActions.select),
  //
  // ));

  // list Results Views
  loadViews$ = createEffect(() => this.actions$.pipe(
    ofType(listResultsViews),
    switchMap(({ options, selectDefault }) => this.api.listForceTableSettings(options).pipe(
      this.handler.effectResponse({
        retry: listResultsViews({ options }),
        success: (response: ListResultsViewResponse) => listResultsViewsSuccess({ response, selectDefault }),
        failure: listResultsViewsFailure(),
        failureMessage: 'Failed to retrieve results views',
      })
    ))
  ));

  // Retrieve Results View in full
  loadSelected$ = createEffect(() => this.actions$.pipe(
    ofType(selectResultsView),
    filter((payload): payload is {uuid: string} & Action => !!payload.uuid),
    switchMap(({ uuid }) => this.api.retrieveForceTableSetting(uuid).pipe(
      this.handler.effectResponse({
        success: (selectedView: ResultsView) => selectResultsViewSuccess({ selectedView }),
        retry: selectResultsView({ uuid }),
        failure: selectResultsViewFailure(),
        failureMessage: 'Failed to load results view',
      })
    ))
  ));

  // When retrieved set a reults view as lat used
  triggerSetAsLastUsed$ = createEffect(() => this.actions$.pipe(
    ofType(selectResultsViewSuccess),
    map(({ selectedView }) => setLastUsedResultsView({ uuid: selectedView.uuid }))
  ));

  setLastUsed$ = createEffect(() => this.actions$.pipe(
    ofType(setLastUsedResultsView),
    concatLatestFrom(() => this.store.select(selectCurrentProjectId)),
    filter((payload): payload is [ReturnType<typeof setLastUsedResultsView>, string] => !!payload[1]),
    switchMap(([{ uuid }, project]) => this.api.setLastUsedForceTableSettings(project, uuid).pipe(
      this.handler.effectResponse({
        retry: setLastUsedResultsView({ uuid }),
        success: (updatedLastUsed: LastUsedForceTableSettings) => setLastUsedResultsViewSuccess({ updatedLastUsed }),
        failure: setLastUsedResultsViewFailure(),
      })
    ))
  ));


  // When a sandbox is selected select it's results view to trigger loading of the data
  loadSandboxSelectedView$ = createEffect(() => this.actions$.pipe(
    ofType(selectSandbox),
    concatLatestFrom(() => [
      this.store.select(selectSandboxEntities),
      this.store.select(selectSelectedResultsView),
    ]),
    map(([{ selectedId }, entities, selectedView]) => {
      const selectedSandbox: Sandbox|undefined = entities[selectedId];
      if (selectedSandbox?.force_settings) {
        return selectResultsView({ uuid: selectedSandbox.force_settings });
      } else if (selectedView) {
        return selectResultsView({ uuid: selectedView?.uuid });
      } else {
        return clearSelectedResultsView();
      }
    })
  ));

  // Update the current View
  updateCurrentView$ = createEffect(() => this.actions$.pipe(
    ofType(selectResultsViewSuccess, resultsResolverSelectDefaultView),
    map(({ selectedView }) =>
      setCurrentView({ textColumns: selectedView.text_columns, data: selectedView.data }))
  ));

  // Reload the selected view
  reloadSelected$ = createEffect(() => this.actions$.pipe(
    ofType(reloadSelectedResultsView),
    concatLatestFrom(() => this.store.select(selectSelectedResultsView)),
    filter((payload): payload is [ReturnType<typeof reloadSelectedResultsView>, ResultsView] => !!payload[1]),
    map(([, { uuid }]: [any, ResultsView]) => selectResultsView({ uuid }))
  ));

  /**
   * UPDATE ACTIONS
   */

  // Update the selected results view
  updateSelectedView$ = createEffect(() => this.actions$.pipe(
    ofType(updateResultsView),
    switchMap(( { view }) => this.api.updateForceTableSettings(view).pipe(
      this.handler.effectResponse({
        success: (selectedView: ResultsView) => updateResultsViewSuccess({ selectedView }),
        retry: updateResultsView({ view }),
        failure: updateResultsViewFailure(),
        failureMessage: 'Failed to update results view',
      })
    ))
  ));

  /**
   * DELETE ACTIONS
   */

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(deleteResultsView),
    switchMap(({ uuid }) => this.api.deleteForceTableSettings(uuid).pipe(
      this.handler.effectResponse({
        retry: deleteResultsView({ uuid }),
        success: () => deleteResultsViewSuccess({ uuid }),
        failure: deleteResultsViewFailure(),
        failureMessage: 'Failed to delete results view',
      })
    ))
  ));

  // Clear the selected results view if it is deleted
  deleteAndClear$ = createEffect(() => this.actions$.pipe(
    ofType(deleteResultsViewSuccess),
    concatLatestFrom(() => this.store.select(selectSelectedResultsView)),
    filter(([{ uuid }, selectedView]) => selectedView?.uuid === uuid),
    map(() => clearSelectedResultsView())
  ));


  /**
   * Select Views
   */

  selectProjectDefaultView$ = createEffect(() => this.actions$.pipe(
    ofType(selectProjectDefaultResultsView),
    concatLatestFrom(() => [
      this.store.select(selectCurrentProjectId),
      this.store.select(selectAllResultsViews),
    ]),
    filter((payload): payload is [Action, string, ResultsView[]] => !!payload[1] && !!payload[2]),
    map(([, project, views]) => {
      const projectDefault = views.find(view => view.default_for_projects.includes(project));
      if (projectDefault) {
        return selectResultsView({ uuid: projectDefault.uuid });
      }

      const lastUsed = views.find(view => view.last_used);
      if (lastUsed) {
        return selectResultsView({ uuid: lastUsed.uuid });
      }

      return clearSelectedResultsView();
    })
  ));

  // selectDefaultViewOnLoadSuccess$ = createEffect(() => this.actions$.pipe(
  //   ofType(listResultsViewsSuccess),
  //   filter(({ selectDefault }) => selectDefault),
  //   map(({ response }) => response.current_default
  //     ? selectResultsView({ uuid: response.current_default })
  //     : clearSelectedResultsView()
  //   ),
  // ));

  /**
   * Misc
   */

  pin$ = createEffect(() => this.actions$.pipe(
    ofType(pinResultsView),
    switchMap(({ uuid, project, pinned }) => this.api.starForceTableSetting(uuid, project, pinned).pipe(
      this.handler.effectResponse({
        retry: pinResultsView({ uuid, project, pinned }),
        success: (updatedView: ResultsViewListData) => pinResultsViewSuccess({ updatedView }),
        failure: pinResultsViewFailure(),
        failureMessage: 'Failed to pin results view',
      })
    ))
  ));

  projectDefault$ = createEffect(() => this.actions$.pipe(
    ofType(setResultsViewAsProjectDefault),
    switchMap(({ uuid, project, as_default }) => this.api.setForceTableSettingAsProjectDefault(uuid, project, as_default).pipe(
      this.handler.effectResponse({
        retry: setResultsViewAsProjectDefault({ uuid, project, as_default }),
        success: (updatedView: ResultsViewListData) => setResultsViewAsProjectDefaultSuccess({ updatedView, project, as_default }),
        failure: setResultsViewAsProjectDefaultFailure(),
        failureMessage: 'Failed to set results view as project default',
      })
    ))
  ));

  onSetDeltaBaseline$ = createEffect(() => this.actions$.pipe(
    ofType(setCurrentViewDeltaBaseline),
    concatLatestFrom(() => this.store.select(selectSelectedSandboxId)),
    map(([, sandboxSelected]) => {
      if (sandboxSelected) {
        return loadSandboxResults({ uuid: sandboxSelected });
      } else {
        return reloadAllResults();
      }
    })
  ));

  onSetDeltaToLeadCase$ = createEffect(() => this.actions$.pipe(
    ofType(toggleCurrentViewDeltaToLeadCase),
    concatLatestFrom(() => this.store.select(selectSelectedSandboxId)),
    map(([, sandboxSelected]) => {
      if (sandboxSelected) {
        return loadSandboxResults({ uuid: sandboxSelected });
      } else {
        return reloadAllResults();
      }
    })
  ));

  reorderPinnedViews = createEffect(() => this.actions$.pipe(
    ofType(reorderPinnedViews),
    concatLatestFrom(() => this.store.select(selectCurrentProjectId)),
    filter((payload): payload is [ReturnType<typeof reorderPinnedViews>, string] => !!payload[1]),
    switchMap(([{ settingsList }, projectId]) =>
      this.api.reorderPinnedForceTableSettings(projectId, settingsList).pipe(
        this.handler.effectResponse({
          retry: reorderPinnedViews({ settingsList }),
          success: () => reorderPinnedViewsSuccess({ settingsList, projectId }),
          failure: reorderPinnedViewsFailure(),
          failureMessage: 'Could not reorder pinned views'
        })
      )
    )
  ));

  /**
   * Component-Based Effects
   */


  loadResultsComponentViews$ = createEffect(() => this.actions$.pipe(
    ofType(resultsComponentLoad, resultsComponentRefresh),
    map(() => listResultsViews({})),
  ));

  // Reload the selected results view when the component is refreshed
  refreshResultsComponentSelectedView$ = createEffect(() => this.actions$.pipe(
    ofType(resultsComponentRefresh),
    concatLatestFrom(() => this.store.select(selectSelectedResultsView)),
    filter(([, selectedView]) => !!selectedView),
    map(() => reloadSelectedResultsView())
  ));
}
